import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Slideout,
  Typography,
  Button,
  ButtonsList,
  Icon,
  CircularProgress,
  Scrollbar,
} from "@ann/web-components";
import { NotesContent } from "./components";
import {
  SlideoutContainer,
  ContentArea,
  HeaderTitleWrapper,
  Subtitle,
  FiltersWrapper,
  ListWrapper,
  ScrollWrapper,
  ScrollContent,
  EmptyState,
  EmptyStateContent,
} from "./NotesViewAllSlideout.styled";
import NotesSlideOut from "../../../Activity/components/PreviewSlideOut/NotesSlideOut";

const filterLabels = [
  "All",
  "Opportunity",
  "Urgent",
  "General",
  "Follow-up",
  "Category 4",
];

const NotesViewAllSlideout = ({
  open,
  onClose,
  notes = [],
  loading,
  error,
  onRetry,
  onNavigateToNotes = () => {},
  onCreateNote,
  onDeleteNote,
}) => {
  const [selectedFilter, setSelectedFilter] = useState(filterLabels[0]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedFilter(filterLabels[0]);
    }
  }, [open]);

  const filterItems = useMemo(
    () =>
      filterLabels.map((label) => ({
        label,
        selected: label === selectedFilter,
        onClick: () => setSelectedFilter(label),
      })),
    [selectedFilter]
  );

  const handleOpenCreate = useCallback(() => {
    setIsCreateOpen(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setIsCreateOpen(false);
  }, []);

  const renderFallback = () => {
    if (loading && notes.length === 0) {
      return (
        <EmptyState>
          <EmptyStateContent>
            <CircularProgress size="small" />
          </EmptyStateContent>
        </EmptyState>
      );
    }

    if (error) {
      return (
        <EmptyState>
          <EmptyStateContent>
            <Typography category="subtitle" weight="bold" size="medium">
              We couldn’t load notes.
            </Typography>
            <Typography category="body" weight="regular" size="small">
              Please try again in a moment.
            </Typography>
            <Button
              label="Try again"
              type="primary"
              size="medium"
              onClick={onRetry}
            />
          </EmptyStateContent>
        </EmptyState>
      );
    }

    if (notes.length === 0) {
      return (
        <EmptyState>
          <EmptyStateContent>
            <Icon name="description" size="large" color="primary" />
            <Typography category="subtitle" weight="bold" size="medium">
              No notes yet
            </Typography>
            <Typography category="body" weight="regular" size="small">
              Add notes to track preferences or key info.
            </Typography>
            <Button
              label="Create"
              type="primary"
              size="medium"
              onClick={handleOpenCreate}
            />
          </EmptyStateContent>
        </EmptyState>
      );
    }

    return null;
  };

  const fallbackContent = renderFallback();
  const showListContent = fallbackContent === null;
  const showFooter = showListContent && notes.length > 0 && !error;

  return (
    <>
      <Slideout open={open} onClose={onClose} size="large">
        <Slideout.Content>
          <SlideoutContainer>
            <HeaderTitleWrapper>
              <Typography category="title" weight="bold" size="large">
                Your Notes
              </Typography>
              <Subtitle category="body" weight="regular" size="small">
                All your client-specific observations.
              </Subtitle>
            </HeaderTitleWrapper>
            <FiltersWrapper>
              <ButtonsList items={filterItems} size="small" carousel />
            </FiltersWrapper>
            <ContentArea>
              {showListContent ? (
                <ScrollWrapper>
                  <Scrollbar size="small" style={{ height: "100%" }}>
                    <ScrollContent>
                      <ListWrapper>
                        <NotesContent
                          notes={notes}
                          loading={loading}
                          error={error}
                          onRetry={onRetry}
                          onDeleteNote={onDeleteNote}
                        />
                      </ListWrapper>
                    </ScrollContent>
                  </Scrollbar>
                </ScrollWrapper>
              ) : (
                fallbackContent
              )}
            </ContentArea>
          </SlideoutContainer>
        </Slideout.Content>
        {showFooter && (
          <Slideout.Actions>
            <Button
              label="View All"
              type="secondary"
              size="medium"
              onClick={onNavigateToNotes}
            />
            <Button
              label="Create"
              type="primary"
              size="medium"
              onClick={handleOpenCreate}
            />
          </Slideout.Actions>
        )}
      </Slideout>
      <NotesSlideOut
        open={isCreateOpen}
        onClose={handleCloseCreate}
        showBackButton
        mode="create"
        onCreateNote={onCreateNote}
      />
    </>
  );
};

export default NotesViewAllSlideout;
